import { FileCodeIcon } from "@phosphor-icons/react/dist/csr/FileCode";
import { FileDocIcon } from "@phosphor-icons/react/dist/csr/FileDoc";
import { FilePdfIcon } from "@phosphor-icons/react/dist/csr/FilePdf";
import { FileTextIcon } from "@phosphor-icons/react/dist/csr/FileText";
import { FileXlsIcon } from "@phosphor-icons/react/dist/csr/FileXls";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { cn } from "@volca/ui/lib/utils";
import { AttachmentImage } from "./attachment-image";
import { isImageAttachment, type Attachment } from "./enrich-attachments";

export const AttachmentTile = ({
  file,
  disabled = false,
  onInspect,
  onRemove,
  onRetry,
}: {
  file: Attachment;
  disabled?: boolean;
  onInspect: () => void;
  onRemove?: () => void;
  onRetry?: () => void;
}) => {
  const extension = file.name.split(".").at(-1)?.toLowerCase();
  const Icon =
    extension === "pdf"
      ? FilePdfIcon
      : ["xlsx", "xls", "csv"].includes(extension ?? "")
        ? FileXlsIcon
        : extension === "docx"
          ? FileDocIcon
          : ["json", "xml"].includes(extension ?? "")
            ? FileCodeIcon
            : FileTextIcon;
  return (
    <li
      className={cn(
        "relative w-28 min-w-0 shrink-0 overflow-hidden rounded-lg border border-line bg-surface-raised",
        {
          "border-danger-line bg-danger-subtle": !!file.error || file.notUsed,
        },
      )}
    >
      <button
        type="button"
        onClick={onInspect}
        aria-label={`Inspect ${file.name}`}
        className="block w-full text-left outline-none focus-visible:ring-2 focus-visible:ring-focus focus-visible:ring-inset"
      >
        {isImageAttachment(file) ? (
          <AttachmentImage file={file} />
        ) : (
          <Stack
            align="center"
            justify="center"
            className="h-16 bg-surface-sunken text-subtle"
          >
            <Icon aria-hidden className="size-8" weight="duotone" />
          </Stack>
        )}
        <Stack
          gap={0}
          className={cn("border-t border-line px-2 py-1.5 text-xs", {
            "border-danger-line text-danger": !!file.error || file.notUsed,
          })}
          title={file.name}
        >
          <span className="block truncate">{file.name}</span>
          {file.notUsed && <span className="font-medium">Not used</span>}
        </Stack>
      </button>
      {onRemove && (
        <Button
          type="button"
          size="icon-xs"
          variant="secondary"
          className="absolute top-1 right-1"
          aria-label={`Remove ${file.name}`}
          disabled={disabled}
          onClick={onRemove}
        >
          <XIcon className="size-3" />
        </Button>
      )}
      {!!file.error && (
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                type="button"
                size="icon-xs"
                variant="secondary"
                color="danger"
                className="absolute top-1 left-1"
                aria-label={`File error for ${file.name}`}
                onClick={onInspect}
              />
            }
          >
            <WarningCircleIcon aria-hidden weight="fill" />
          </TooltipTrigger>
          <TooltipContent>{file.error}</TooltipContent>
        </Tooltip>
      )}
      {onRetry && (
        <Button
          type="button"
          size="xs"
          variant="ghost"
          disabled={disabled}
          aria-label={`Retry ${file.name}`}
          onClick={onRetry}
        >
          Retry
        </Button>
      )}
    </li>
  );
};
